import { useState, useMemo } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "react-query"
import { useTranslation } from "react-i18next"
import { BasicTable, BetaBadge, Button, ConfirmDialog, DialogView, Notification, PageView, PageTopBar } from "../../../components"
import { SelectColumnFilter } from "../../../components/BasicTable/modules/ColumnFiltersUI"
import { Loader } from "../../../components/Loader"
import { fetchStores } from "../../../services/storeService"
import { fetchContainerTemplates, deleteContainerTemplate } from "../../../services/containerTemplateServices"
import { CONFIGURATIONS_CONTAINER_TEMPLATE_CREATE, CONFIGURATIONS_CONTAINER_TEMPLATE_EDITOR } from "../../../navigation/constants"
import { PencilIcon, TrashIcon, PrinterIcon } from "@heroicons/react/outline"
import { GenerateTemplateLabel } from "./components/GenerateTemplateLabel"

export const TemplatesListContainer = () => {
    const { i18n } = useTranslation()
    const history = useHistory()
    const { warehouse_id } = useParams()
    const queryClient = useQueryClient()

    const [selectedStoreId, setSelectedStoreId] = useState(null)
    const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null })
    const [notification, setNotification] = useState({ show: false, type: '', title: '' })
    const [labelDialog, setLabelDialog] = useState({ open: false, groupingCode: '', name: '' })

    // Fetch stores for selector
    const { data: storesData } = useQuery(
        ['stores', warehouse_id],
        () => fetchStores({ warehouse_id }),
        { refetchOnWindowFocus: false }
    )

    // Fetch templates for selected store
    const { data: templatesData, isLoading: templatesLoading, isFetching } = useQuery(
        ['container-content-templates', selectedStoreId],
        () => fetchContainerTemplates(selectedStoreId),
        {
            enabled: !!selectedStoreId,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    )

    // Delete mutation
    const deleteMutation = useMutation(
        (templateId) => deleteContainerTemplate(selectedStoreId, templateId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['container-content-templates', selectedStoreId])
                setDeleteDialog({ open: false, template: null })
                showNotification('success', i18n.t('container_templates.notifications.deleted'))
            },
            onError: (error) => {
                showNotification('error', error?.response?.data?.error || i18n.t('container_templates.notifications.error_delete'))
            }
        }
    )

    const showNotification = (type, title) => {
        setNotification({ show: true, type, title })
    }

    const handleEdit = (template) => {
        const path = CONFIGURATIONS_CONTAINER_TEMPLATE_EDITOR
            .replace(':warehouse_id', warehouse_id)
            .replace(':template_id', template.id)
        // store_id in the query string so the editor survives a refresh / direct link.
        history.push({ pathname: path, search: `?store_id=${selectedStoreId}` }, { template, storeId: selectedStoreId })
    }

    const handleCreate = () => {
        const path = CONFIGURATIONS_CONTAINER_TEMPLATE_CREATE.replace(':warehouse_id', warehouse_id)
        history.push({ pathname: path, search: `?store_id=${selectedStoreId}` }, { storeId: selectedStoreId })
    }

    const handleDelete = (template) => {
        setDeleteDialog({ open: true, template })
    }

    const handlePrintLabel = (template) => {
        setLabelDialog({ open: true, groupingCode: template.grouping_code, name: template.name || '' })
    }

    const confirmDelete = () => {
        if (deleteDialog.template) {
            deleteMutation.mutate(deleteDialog.template.id)
        }
    }

    // Table columns
    const activeFilterOptions = useMemo(() => [
        { id: 'true', label: i18n.t('common.yes') },
        { id: 'false', label: i18n.t('common.no') },
    ], [i18n])

    const typeFilterOptions = useMemo(() => [
        { id: 'GROUP_CONTAINER', label: i18n.t('container_templates.container_types.GROUP_CONTAINER') },
        { id: 'PRODUCT_CONTAINER', label: i18n.t('container_templates.container_types.PRODUCT_CONTAINER') },
    ], [i18n])

    const columns = useMemo(() => [
        {
            Header: i18n.t('container_templates.headers.grouping_code'),
            accessor: 'grouping_code',
            disableSortBy: false,
        },
        {
            Header: i18n.t('container_templates.headers.name'),
            accessor: 'name',
            disableSortBy: false,
        },
        {
            Header: i18n.t('container_templates.headers.container_type'),
            accessor: 'container_type',
            disableFilters: false,
            disableSortBy: true,
            Filter: SelectColumnFilter,
            selectFilterOptions: typeFilterOptions,
        },
        {
            Header: i18n.t('container_templates.headers.items_count'),
            accessor: 'items_count',
            disableSortBy: true,
        },
        {
            Header: i18n.t('container_templates.headers.active'),
            accessor: 'active',
            disableFilters: false,
            disableSortBy: true,
            Filter: SelectColumnFilter,
            selectFilterOptions: activeFilterOptions,
        },
        {
            Header: i18n.t('container_templates.headers.actions'),
            accessor: 'actions',
            disableSortBy: true,
        },
    ], [i18n, typeFilterOptions, activeFilterOptions])

    // Table data
    const templates = useMemo(() => {
        const raw = templatesData?.container_content_templates ?? templatesData
        return Array.isArray(raw) ? raw : []
    }, [templatesData])
    const tableData = useMemo(() => {
        return templates.map((template) => ({
            grouping_code: template.grouping_code,
            name: template.name || '-',
            container_type: (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    template.container_type === 'GROUP_CONTAINER'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                }`}>
                    {i18n.t(`container_templates.container_types.${template.container_type}`)}
                </span>
            ),
            items_count: template.container_content_template_items?.length || 0,
            active: template.active ? i18n.t('common.yes') : i18n.t('common.no'),
            actions: (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => { e.stopPropagation(); handlePrintLabel(template) }}
                        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                    >
                        <PrinterIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(template) }}
                        className="p-1 text-gray-500 hover:text-indigo-600 transition-colors"
                    >
                        <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(template) }}
                        className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                    >
                        <TrashIcon className="h-5 w-5" />
                    </button>
                </div>
            ),
        }))
    }, [templates, i18n])

    const stores = storesData?.stores || storesData || []

    return (
        <PageView
            topMenu={
                <PageTopBar>
                    <div className="text-lg font-semibold flex items-center gap-2">
                        {i18n.t('container_templates.title')}
                        <BetaBadge />
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <select
                            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={selectedStoreId || ''}
                            onChange={(e) => setSelectedStoreId(e.target.value || null)}
                        >
                            <option value="">{i18n.t('container_templates.select_store')}</option>
                            {(Array.isArray(stores) ? stores : []).map((store) => (
                                <option key={store.id} value={store.id}>{store.name}</option>
                            ))}
                        </select>
                        {selectedStoreId && (
                            <Button type="primary" onClick={handleCreate}>
                                {i18n.t('container_templates.create_template')}
                            </Button>
                        )}
                    </div>
                </PageTopBar>
            }
            childrenFullWidth={true}
            topMenuFullWidth={true}
        >
            <Notification
                show={notification.show}
                setShow={(val) => setNotification(prev => ({ ...prev, show: val }))}
                type={notification.type}
                title={notification.title}
            />

            {!selectedStoreId ? (
                <div className="text-center py-12 text-gray-500">
                    {i18n.t('container_templates.select_store')}
                </div>
            ) : templatesLoading ? (
                <Loader show={true} />
            ) : (
                <BasicTable
                    columns={columns}
                    data={tableData}
                    filterable={true}
                    sortable={true}
                    showHeader={true}
                    showLoader={isFetching}
                    isFetching={isFetching}
                    emptyTableText={i18n.t('container_templates.no_templates')}
                />
            )}

            <ConfirmDialog
                open={deleteDialog.open}
                setOpen={(val) => setDeleteDialog(prev => ({ ...prev, open: val }))}
                title={i18n.t('container_templates.delete_template')}
                description={i18n.t('container_templates.confirm_delete')}
                confirmLabel={i18n.t('container_templates.delete_template')}
                cancelLabel={i18n.t('container_templates.cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, template: null })}
                loading={deleteMutation.isLoading}
                type="danger"
            />

            <DialogView className="w-full" setOpen={(val) => setLabelDialog(prev => ({ ...prev, open: val }))} open={labelDialog.open}>
                <GenerateTemplateLabel
                    groupingCode={labelDialog.groupingCode}
                    templateName={labelDialog.name}
                    onCancel={() => setLabelDialog({ open: false, groupingCode: '', name: '' })}
                    onSuccess={() => setLabelDialog({ open: false, groupingCode: '', name: '' })}
                />
            </DialogView>
        </PageView>
    )
}
